import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Checkbox as MuiCheckbox,
    Tooltip,
} from '@mui/material';
import Calling from '../../api/Calling';
import TableSkeleton from '../../components/ul/TableSkeleton';

import {
    Plus,
    Pencil,
    Trash2,
    Phone,
    Search,
    X,
    Check,
    AlertCircle,
    ChevronUp,
    ChevronDown,
    Hash,
} from 'lucide-react';
import generateCaptcha from '../../components/captcha';
import formatDate from '../../components/formatDate';

const WhiteListNumber = () => {
    // State management
    const [loader, setLoader] = useState(true);
    const [whitelistData, setWhitelistData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    // Sorting states
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

    // Selection states for bulk delete
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    // Form states
    const [formData, setFormData] = useState({ title: '', mobileNumber: '' });
    const [editId, setEditId] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch whitelist data
    const fetchWhitelistData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getWhitelistNumber();
            if (response?.status) {
                setWhitelistData(response?.data || []);
            } else {
                toast.error(response?.message || 'Failed to fetch whitelist data');
            }
        } catch (error) {
            console.error('Error fetching whitelist:', error);
            toast.error('Failed to fetch whitelist data');
        } finally {
            setLoader(false);
            setSelectedIds([]);
            setSelectAll(false);
        }
    };

    useEffect(() => {
        fetchWhitelistData();
    }, []);

    // Search and Sort filter
    useEffect(() => {
        let result = [...whitelistData];

        // Apply search filter
        if (searchQuery.trim() !== '') {
            result = result.filter(item =>
                item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.mobileNumber?.includes(searchQuery)
            );
        }

        // Apply sorting
        result.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            // Handle string comparison (case-insensitive)
            if (sortField === 'title' || sortField === 'mobileNumber') {
                aValue = (aValue || '').toLowerCase();
                bValue = (bValue || '').toLowerCase();
            }

            // Handle date comparison
            if (sortField === 'createdAt') {
                aValue = new Date(aValue || 0).getTime();
                bValue = new Date(bValue || 0).getTime();
            }

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredData(result);

        // Reset selection when data changes
        setSelectedIds([]);
        setSelectAll(false);
    }, [searchQuery, whitelistData, sortField, sortOrder]);

    // Handle sort
    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle sort order if same field
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field with default ascending order
            setSortField(field);
            setSortOrder('asc');
        }
    };



    // Handle select all checkbox
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredData.map(item => item._id));
        }
        setSelectAll(!selectAll);
    };

    // Handle individual checkbox
    const handleSelectItem = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(itemId => itemId !== id));
            setSelectAll(false);
        } else {
            const newSelected = [...selectedIds, id];
            setSelectedIds(newSelected);
            if (newSelected.length === filteredData.length) {
                setSelectAll(true);
            }
        }
    };

    // Form validation
    const validateForm = () => {
        const errors = {};

        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        } else if (formData.title.length < 2) {
            errors.title = 'Title must be at least 2 characters';
        }

        if (!formData.mobileNumber.trim()) {
            errors.mobileNumber = 'Mobile number is required';
        } else if (!/^[0-9]+$/.test(formData.mobileNumber)) {
            errors.mobileNumber = 'Enter a valid mobile number';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'mobileNumber') {
            // Only allow numbers
            const numericValue = value.replace(/[^0-9]/g, '');
            setFormData(prev => ({ ...prev, [name]: numericValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Add new whitelist number
    const handleAdd = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const body = {
                title: formData.title.trim(),
                mobileNumber: formData.mobileNumber
            }

            const response = await Calling.addWhitelistNumber(body);
            if (response?.status) {
                toast.success(response?.message || 'Number added successfully');
                setShowAddModal(false);
                resetForm();
                fetchWhitelistData();
            } else {
                toast.error(response?.message || 'Failed to add number');
            }
        } catch (error) {
            console.error('Error adding whitelist number:', error);
            toast.error('Failed to add number');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Edit whitelist number
    const handleEdit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const body = {
                title: formData.title.trim(),
                mobileNumber: formData.mobileNumber
            }

            const response = await Calling.updateWhitelistNumber(editId, body);

            if (response?.status) {
                toast.success(response?.message || 'Number updated successfully');
                setShowEditModal(false);
                resetForm();
                fetchWhitelistData();
            } else {
                toast.error(response?.message || 'Failed to update number');
            }
        } catch (error) {
            console.error('Error updating whitelist number:', error);
            toast.error('Failed to update number');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete whitelist numbers (single or bulk)
    const handleDelete = async (idsToDelete) => {
        const count = idsToDelete.length;
        const content = `You are about to delete ${count} record(s). To confirm, please solve this math problem`;
        const check = generateCaptcha(content);
        if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

        setLoader(true);
        try {
            const body = { ids: idsToDelete };
            const response = await Calling.deleteWhitelistNumber(body);

            if (response?.status) {
                toast.success(response?.message || `${count} record(s) deleted successfully`);
                fetchWhitelistData();
            } else {
                toast.error(response?.message || 'Failed to delete record(s)');
                setLoader(false);
            }
        } catch (error) {
            console.error('Error deleting whitelist number(s):', error);
            toast.error('Failed to delete record(s)');
            setLoader(false);
        }
    };

    // Delete single whitelist number
    const handleDeleteSingle = (id) => {
        handleDelete([id]);
    };

    // Bulk delete whitelist numbers
    const handleBulkDelete = () => {
        if (selectedIds.length === 0) {
            return toast.error('Please select at least one record to delete');
        }
        handleDelete(selectedIds);
    };

    // Open edit modal
    const openEditModal = (item) => {
        setFormData({
            title: item.title || '',
            mobileNumber: item.mobileNumber || ''
        });
        setEditId(item._id);
        setShowEditModal(true);
    };

    // Reset form
    const resetForm = () => {
        setFormData({ title: '', mobileNumber: '' });
        setFormErrors({});
        setEditId(null);
    };

    // Close modals
    const closeAddModal = () => {
        setShowAddModal(false);
        resetForm();
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        resetForm();
    };



    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full min-h-screen bg-main-bg text-main-font rounded-2xl"
        >
            <div className="p-4">
                {/* Header Section */}
                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6 mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-main-th rounded-xl flex items-center justify-center border border-main-blue/20">
                                <Phone className="text-main-blue" size={22} />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold text-main-font">Whitelist Numbers</h1>
                                <p className="text-sm text-main-subfont mt-0.5">
                                    Manage whitelisted phone numbers ({whitelistData.length} total)
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 bg-main-blue hover:bg-[#2db84d] text-main-white px-5 py-3 rounded-xl font-semibold transition-all shadow-md shadow-main-blue/25 text-sm"
                        >
                            <Plus size={18} />
                            Add Number
                        </button>
                    </div>
                </div>

                {/* Search & Bulk Actions Section */}
                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                        {/* Search */}
                        <div className="flex-1">
                            <TextField
                                fullWidth
                                size="small"
                                type="text"
                                placeholder="Search by title or mobile number..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: '#fff',
                                    },
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={20} className="text-main-subfont" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchQuery ? (
                                            <InputAdornment position="end">
                                                <Tooltip title="Clear">
                                                    <IconButton
                                                        onClick={() => setSearchQuery('')}
                                                        size="small"
                                                        edge="end"
                                                    >
                                                        <X size={18} className="text-main-subfont" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                            />
                        </div>

                        {/* Bulk Delete Button */}
                        {selectedIds.length > 0 && (
                            <button
                                onClick={handleBulkDelete}
                                className="flex items-center gap-2 bg-main-delete hover:bg-main-delete/90 text-main-white px-5 py-3 rounded-xl font-semibold transition-all shadow-md text-sm"
                            >
                                <Trash2 size={18} />
                                Delete Selected ({selectedIds.length})
                            </button>
                        )}
                    </div>

                    {/* Selection Info */}
                    {selectedIds.length > 0 && (
                        <div className="mt-4 px-4 py-3 bg-main-th rounded-xl text-sm text-main-blue flex items-center justify-between border border-main-blue/20">
                            <span className="font-medium">{selectedIds.length} of {filteredData.length} record(s) selected</span>
                            <button
                                onClick={() => {
                                    setSelectedIds([]);
                                    setSelectAll(false);
                                }}
                                className="text-main-blue hover:text-[#2db84d] font-semibold transition-colors"
                            >
                                Clear Selection
                            </button>
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm overflow-hidden">
                    {loader ? (
                        <TableSkeleton rows={6} cols={5} />
                    ) : filteredData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-4">
                            <div className="w-24 h-24 bg-main-th rounded-full flex items-center justify-center mb-5 border-2 border-main-blue/20">
                                <Phone size={36} className="text-main-blue" />
                            </div>
                            <h3 className="text-lg font-bold text-main-font mb-1">
                                {searchQuery ? 'No results found' : 'No whitelist numbers'}
                            </h3>
                            <p className="text-sm text-main-subfont text-center max-w-sm">
                                {searchQuery
                                    ? `No numbers match "${searchQuery}". Try a different search.`
                                    : 'Add your first whitelist number to get started.'}
                            </p>
                            {!searchQuery && (
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="mt-5 flex items-center gap-2 text-main-blue hover:text-[#2db84d] font-semibold transition-colors px-4 py-2 rounded-xl border border-main-blue/20 hover:bg-main-th"
                                >
                                    <Plus size={18} />
                                    Add Number
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="divide-y divide-main-border">
                            {/* Table Header - Desktop */}
                            <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-4 bg-main-th text-sm font-semibold text-main-font border-b border-main-border">
                                <div className="col-span-1 flex items-center gap-3">
                                    <Checkbox
                                        checked={selectAll}
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredData.length}
                                        onChange={handleSelectAll}
                                    />
                                    <span>#</span>
                                </div>
                                <div className="col-span-4">
                                    <SortableHeader field="title" label="Title" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} />
                                </div>
                                <div className="col-span-3">
                                    <SortableHeader field="mobileNumber" label="Mobile Number" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} />
                                </div>
                                <div className="col-span-2">
                                    <SortableHeader field="createdAt" label="Created" handleSort={handleSort} sortField={sortField} sortOrder={sortOrder} />
                                </div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>

                            {/* Mobile Sort Options */}
                            <div className="md:hidden px-4 py-3 bg-main-th border-b border-main-border">
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-main-subfont font-medium">Sort by:</span>
                                    <select
                                        value={`${sortField}-${sortOrder}`}
                                        onChange={(e) => {
                                            const [field, order] = e.target.value.split('-');
                                            setSortField(field);
                                            setSortOrder(order);
                                        }}
                                        className="px-3 py-2 border border-main-border rounded-xl text-main-font bg-main-white focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue"
                                    >
                                        <option value="title-asc">Title (A-Z)</option>
                                        <option value="title-desc">Title (Z-A)</option>
                                        <option value="mobileNumber-asc">Mobile (Asc)</option>
                                        <option value="mobileNumber-desc">Mobile (Desc)</option>
                                        <option value="createdAt-desc">Newest First</option>
                                        <option value="createdAt-asc">Oldest First</option>
                                    </select>
                                </div>
                            </div>

                            {/* List Items */}
                            {filteredData.map((item, index) => (
                                <div
                                    key={item._id}
                                    className={`group transition-colors duration-200 ${selectedIds.includes(item._id)
                                        ? 'bg-main-th'
                                        : 'hover:bg-main-bg'
                                        }`}
                                >
                                    {/* Desktop View */}
                                    <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-4 items-center">
                                        <div className="col-span-1 flex items-center gap-3">
                                            <Checkbox
                                                checked={selectedIds.includes(item._id)}
                                                onChange={() => handleSelectItem(item._id)}
                                            />
                                            <span className="text-sm text-main-subfont">{index + 1}</span>
                                        </div>
                                        <div className="col-span-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-main-th rounded-full flex items-center justify-center text-main-blue font-bold border border-main-blue/20">
                                                    {item.title?.charAt(0)?.toUpperCase() || 'W'}
                                                </div>
                                                <span className="font-semibold text-main-font">
                                                    {item.title || '-'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col-span-3">
                                            <div className="flex items-center gap-2 text-main-font">
                                                <Phone size={16} className="text-main-subfont" />
                                                <span className="font-mono">{item.mobileNumber || '-'}</span>
                                            </div>
                                        </div>
                                        <div className="col-span-2 text-sm text-main-subfont">
                                            {formatDate(item.createdAt)}
                                        </div>
                                        <div className="col-span-2 flex justify-end gap-2">
                                            <Tooltip title="Edit">
                                                <button
                                                    onClick={() => openEditModal(item)}
                                                    className="p-2.5 text-main-exBlue hover:bg-main-exBlue/10 rounded-xl transition-all border border-transparent hover:border-main-exBlue/20"
                                                >
                                                    <Pencil size={18} />
                                                </button>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <button
                                                    onClick={() => handleDeleteSingle(item._id)}
                                                    className="p-2.5 text-main-delete hover:bg-main-delete/10 rounded-xl transition-all border border-transparent hover:border-main-delete/20"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    {/* Mobile View */}
                                    <div className="md:hidden p-4">
                                        <div className="flex items-start gap-3">
                                            <Checkbox
                                                checked={selectedIds.includes(item._id)}
                                                onChange={() => handleSelectItem(item._id)}
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-12 h-12 bg-main-th rounded-full flex items-center justify-center text-main-blue font-bold text-lg border border-main-blue/20">
                                                            {item.title?.charAt(0)?.toUpperCase() || 'W'}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-main-font">
                                                                {item.title || '-'}
                                                            </h4>
                                                            <div className="flex items-center gap-1.5 text-main-font mt-1">
                                                                <Phone size={14} className="text-main-subfont" />
                                                                <span className="font-mono text-sm">
                                                                    {item.mobileNumber || '-'}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-main-subfont mt-1">
                                                                {formatDate(item.createdAt)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => openEditModal(item)}
                                                            className="p-2 text-main-exBlue hover:bg-main-exBlue/10 rounded-xl transition-all"
                                                        >
                                                            <Pencil size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteSingle(item._id)}
                                                            className="p-2 text-main-delete hover:bg-main-delete/10 rounded-xl transition-all"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Results Count */}
                {!loader && filteredData.length > 0 && (
                    <div className="mt-5 text-sm text-main-subfont text-center">
                        Showing {filteredData.length} of {whitelistData.length} numbers
                    </div>
                )}

                {/* Add Modal */}
                <Modal show={showAddModal} onClose={closeAddModal} title="Add Whitelist Number">
                    <form onSubmit={handleAdd}>
                        <FormInput
                            label="Title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter title (e.g., Office, Home)"
                            error={formErrors.title}
                            icon={Hash}
                        />
                        <FormInput
                            label="Mobile Number"
                            name="mobileNumber"
                            value={formData.mobileNumber}
                            onChange={handleInputChange}
                            placeholder="Enter mobile number"
                            error={formErrors.mobileNumber}
                            icon={Phone}
                        />
                        <div className="flex gap-3 mt-6">
                            <button
                                type="button"
                                onClick={closeAddModal}
                                className="flex-1 px-4 py-3 border border-main-border text-main-font rounded-xl hover:bg-main-bg font-semibold transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-3 bg-main-blue text-main-white rounded-xl hover:bg-[#2db84d] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-main-blue/25"
                            >
                                {isSubmitting ? (
                                    <>
                                        <CircularProgress size={16} color="inherit" />
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <Check size={18} />
                                        Add Number
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Edit Modal */}
                <Modal show={showEditModal} onClose={closeEditModal} title="Edit Whitelist Number">
                    <form onSubmit={handleEdit}>
                        <FormInput
                            label="Title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter title (e.g., Office, Home)"
                            error={formErrors.title}
                            icon={Hash}
                        />
                        <FormInput
                            label="Mobile Number"
                            name="mobileNumber"
                            value={formData.mobileNumber}
                            onChange={handleInputChange}
                            placeholder="Enter mobile number"
                            error={formErrors.mobileNumber}
                            icon={Phone}
                        />
                        <div className="flex gap-3 mt-6">
                            <button
                                type="button"
                                onClick={closeEditModal}
                                className="flex-1 px-4 py-3 border border-main-border text-main-font rounded-xl hover:bg-main-bg font-semibold transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-3 bg-main-blue text-main-white rounded-xl hover:bg-[#2db84d] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md shadow-main-blue/25"
                            >
                                {isSubmitting ? (
                                    <>
                                        <CircularProgress size={16} color="inherit" />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Check size={18} />
                                        Update Number
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </Modal>
            </div>
        </motion.div>
    );
};

export default WhiteListNumber;

// Sort Icon Component
const SortIcon = ({ field, sortField, sortOrder }) => {
    if (sortField !== field) {
        return (
            <div className="flex flex-col ml-1 opacity-40">
                <ChevronUp size={12} className="-mb-1" />
                <ChevronDown size={12} />
            </div>
        );
    }
    return sortOrder === 'asc' ? (
        <ChevronUp size={16} className="ml-1 text-main-blue" />
    ) : (
        <ChevronDown size={16} className="ml-1 text-main-blue" />
    );
};

// Modal Component
const Modal = ({ show, onClose, title, children }) => {
    return (
        <AnimatePresence>
            {show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    ></motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ duration: 0.25 }}
                        className="relative bg-main-white rounded-2xl shadow-2xl w-full max-w-md"
                    >
                        <div className="flex items-center justify-between p-5 border-b border-main-border">
                            <h3 className="text-lg font-bold text-main-font">{title}</h3>
                            <button
                                onClick={onClose}
                                className="w-9 h-9 flex items-center justify-center hover:bg-main-delete/10 rounded-xl transition-all text-main-subfont hover:text-main-delete"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-5">{children}</div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

// Form Input Component
const FormInput = ({ label, name, value, onChange, placeholder, error, icon: Icon, type = 'text', maxLength }) => (
    <div className="mb-4">
        <label className="block text-sm font-semibold text-main-font mb-2">
            {label} <span className="text-main-delete">*</span>
        </label>
        <TextField
            fullWidth
            size="small"
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            error={!!error}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                },
            }}
            slotProps={{
                input: {
                    startAdornment: Icon ? (
                        <InputAdornment position="start">
                            <Icon size={18} className="text-main-subfont" />
                        </InputAdornment>
                    ) : null,
                },
                htmlInput: {
                    ...(maxLength ? { maxLength } : {}),
                },
            }}
        />
        {error && (
            <p className="mt-1 text-sm text-main-delete flex items-center gap-1.5 font-medium">
                <AlertCircle size={14} /> {error}
            </p>
        )}
    </div>
);

// Checkbox Component
const Checkbox = ({ checked, onChange, indeterminate = false }) => (
    <MuiCheckbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={onChange}
        size="small"
        sx={{
            padding: 0,
            color: 'var(--main-border, #d1d5db)',
            '&.Mui-checked': { color: '#34C759' },
            '&.MuiCheckbox-indeterminate': { color: '#34C759' },
        }}
    />
);

// Sortable Header Component
const SortableHeader = ({ field, label, className = '', handleSort, sortField, sortOrder }) => (
    <div
        className={`flex items-center cursor-pointer hover:text-main-blue transition-colors select-none ${className}`}
        onClick={() => handleSort(field)}
    >
        <span>{label}</span>
        <SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
    </div>
);
